import type { SlotRead } from '../api/generated/model'
import { todayAtFacility } from './time'

/** Shared by /book and /checkout, so a link from one works in the other. */

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/
const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/

/** A plain calendar date, 'YYYY-MM-DD'. Deliberately NOT an instant. */
export function isLocalDate(value: unknown): value is string {
  if (typeof value !== 'string' || !DATE_PATTERN.test(value)) return false
  // Rejects 2026-02-31, which passes the regex. Round-tripping through UTC is
  // safe because only the date portion is compared.
  const parsed = new Date(`${value}T00:00:00Z`)
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value
}

export const isStartTime = (value: unknown): value is string =>
  typeof value === 'string' && TIME_PATTERN.test(value)

export interface BookSearch {
  /** Facility-local calendar date. A plain date, so a link shared to WhatsApp
   *  means the same day wherever it is opened. */
  date: string
  /** Bay id. `undefined` means "any", which keeps it out of the URL entirely. */
  bay?: number
  /** Selected start, 'HH:MM'. Absent until the customer picks one. */
  start?: string
  /** What is being bought (decision 0005): a line of the price list, how many
   *  people, how many hours, with which coach. All absent means the default
   *  line for one hour -- see lib/sale.ts. */
  rate?: number
  people?: number
  hours?: number
  coach?: number
}

const positive = (value: unknown, max: number): number | undefined => {
  const n = Number(value)
  return Number.isInteger(n) && n > 0 && n <= max ? n : undefined
}

/**
 * Never throws: a mangled parameter falls back to a sane default rather than
 * showing an error page to somebody who was sent a link in a group chat.
 */
export function parseBookSearch(search: Record<string, unknown>): BookSearch {
  const bay = Number(search.bay)
  return {
    date: isLocalDate(search.date) ? search.date : todayAtFacility(),
    bay: Number.isInteger(bay) && bay > 0 ? bay : undefined,
    start: isStartTime(search.start) ? search.start : undefined,
    rate: positive(search.rate, 1_000_000),
    people: positive(search.people, 20),
    hours: positive(search.hours, 3),
    coach: positive(search.coach, 1_000_000),
  }
}

/** 'HH:MM' plus a whole number of minutes, staying on the clock. */
export function addMinutes(start: string, minutes: number): string {
  const [hours, mins] = start.split(':').map(Number)
  const total = hours * 60 + mins + minutes
  return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}

/** Is this start bookable, for the chosen bay (or for any bay)? */
export const isSlotFree = (slot: SlotRead, bay?: number): boolean =>
  !slot.past && (bay === undefined ? slot.free_bay_ids.length > 0 : slot.free_bay_ids.includes(bay))

/** Group the flat list of 73 starts into [hour, slots] pairs, in order. */
export function groupByHour(starts: SlotRead[]): Array<[string, SlotRead[]]> {
  const groups = new Map<string, SlotRead[]>()
  for (const slot of starts) {
    const hour = slot.start.slice(0, 2)
    const existing = groups.get(hour)
    if (existing) existing.push(slot)
    else groups.set(hour, [slot])
  }
  return [...groups.entries()]
}

/** The first hour with something bookable, so the accordion opens somewhere
 *  useful and the 10-minute options are visible without hunting for them. */
export function firstHourWithSpace(starts: SlotRead[], bay?: number): string | null {
  for (const [hour, slots] of groupByHour(starts)) {
    if (slots.some((slot) => isSlotFree(slot, bay))) return hour
  }
  return null
}
